import React, { useContext } from 'react';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazy, lazyAfterPaint } from '@atlassian/react-loosely-lazy';
import { JiraIssueCreateErrorBoundary } from '@jira/issue-create__issue-create-analytics-common/src/ui/jira-issue-create-error-boundary/index.tsx';
import type { JiraIssueCreateErrorBoundaryProps } from '@jira/issue-create__issue-create-analytics-common/src/ui/jira-issue-create-error-boundary/types.tsx';
import { RichTextEditViewWithLazyEditorConfig } from '@jira/issue-create__issue-create-modal/src/common/ui/fields/description-field/RichTextEditViewWithLazyEditorConfig.tsx';
import { preloadableAsyncRichTextEditEntryPoint } from '@jira/issue__field-rich-text/src/preloadableAsyncRichTextEditEntryPoint.entrypoint.tsx';
import { getViewModeFromLocalStorage } from '@jira/issue__issue-preview-view-mode-storage/src/getViewModeFromLocalStorage.tsx';
import { useTriggerIssueViewModal } from '@jira/issue__open-view-modal-controller/src/useTriggerIssueViewModal.tsx';
import { JiraPreviewPanelsContext } from '@jira/issue__preview-panels/src/JiraPreviewPanelsContext.tsx';
import { fg } from '@jira/platform__feature-gating';
import { BackgroundPriority } from '@jira/platform__services-non-critical/src/BackgroundPriority.tsx';
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component/src/index.tsx';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const JiraGicInviteNudgeListenerLazy = lazy(
	() =>
		import(
			/* webpackChunkName: "async-jira-gic-invite-nudge-listener" */ '@jira/growth__twcg-2043-jira-gic-invite-nudge/src/JiraGicInviteNudgeListener.tsx'
		).then((module) => ({ default: module.JiraGicInviteNudgeListener })),
	{ ssr: false },
);

const JiraGicInviteNudgeListenerLazyWithFG = componentWithFG(
	'jira-gic-invite-nudge-killswitch',
	JiraGicInviteNudgeListenerLazy,
	() => null,
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const CaptureBugContextIssueCreatedListenerLazy = lazy(
	() =>
		import(
			/* webpackChunkName: "async-capture-bug-context-issue-created-listener" */ '@jira/growth__twcg-2199-capture-bug-context/src/CaptureBugContextIssueCreatedListenerWithNavigation.tsx'
		).then(({ CaptureBugContextIssueCreatedListenerWithNavigation }) => ({
			default: CaptureBugContextIssueCreatedListenerWithNavigation,
		})),
	{ ssr: false },
);

const CaptureBugContextListener = () => {
	const modalController = useTriggerIssueViewModal();
	const previewPanels = useContext(JiraPreviewPanelsContext);
	return (
		<CaptureBugContextIssueCreatedListenerLazy
			modalController={modalController}
			previewPanels={previewPanels}
			getStoredViewMode={getViewModeFromLocalStorage}
		/>
	);
};

const CaptureBugContextIssueCreatedListenerLazyWithFG = componentWithFG(
	'capture-bug-context-killswitch',
	CaptureBugContextListener,
	() => null,
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const IssueCreateModernisedModalLazy = lazyAfterPaint(
	() =>
		import(
			/* webpackChunkName: "async-jira-issue-create-modernised-modal" */ './IssueCreateModal.tsx'
		),
	{ ssr: false },
);

export const preloadIssueCreateModernisedModal = (): void => {
	IssueCreateModernisedModalLazy.preload();
	preloadableAsyncRichTextEditEntryPoint.preload();
	if (fg('reduce_gic_bundle')) {
		RichTextEditViewWithLazyEditorConfig.preload();
	}
};

type AsyncIssueCreateModernisedModalProps = {
	onResetApolloCache?: () => void;
	alwaysShowFullForm?: boolean;
	onError?: JiraIssueCreateErrorBoundaryProps['onError'];
};

const IssueCreateModernisedModalWithPriority = ({
	onResetApolloCache,
	alwaysShowFullForm,
}: AsyncIssueCreateModernisedModalProps) => (
	<BackgroundPriority>
		<IssueCreateModernisedModalLazy
			onResetApolloCache={onResetApolloCache}
			alwaysShowFullForm={alwaysShowFullForm}
		/>
		<JiraGicInviteNudgeListenerLazyWithFG />
		<CaptureBugContextIssueCreatedListenerLazyWithFG />
	</BackgroundPriority>
);

export const AsyncIssueCreateModal = ({
	onResetApolloCache,
	alwaysShowFullForm,
	onError,
}: AsyncIssueCreateModernisedModalProps): React.JSX.Element => (
	<JiraIssueCreateErrorBoundary
		id="async.jira.issue-create-modernised-modal"
		packageName="jiraIssueCreateModernised"
		sendToPrivacyUnsafeSplunk
		onError={onError}
	>
		<Placeholder name="issue-create-modernised-modal" fallback={<></>}>
			<IssueCreateModernisedModalWithPriority
				onResetApolloCache={onResetApolloCache}
				alwaysShowFullForm={alwaysShowFullForm}
			/>
		</Placeholder>
	</JiraIssueCreateErrorBoundary>
);
